import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Helmet } from 'react-helmet';
import AdminSidebar from '../../components/ui/AdminSidebar';
import MessageList from './components/MessageList';
import MessageEditor from './components/MessageEditor';
import MessageStats from './components/MessageStats';
import { supabase } from '../../utils/supabase';

const AdminSiteMessages = () => {
  const { user, userProfile } = useAuth();
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeView, setActiveView] = useState('list'); // 'list' or 'edit'
  const [notification, setNotification] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'studio_admin';
    if (isAdmin) {
      loadMessages();
      loadStats();
    }
  }, [userProfile]);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('site_messages')
        .select('*')
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      showNotification('Error loading messages: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data, error } = await supabase
        .from('message_stats')
        .select('*');

      if (error) throw error;
      setStats(data || []);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleSave = async (messageData) => {
    setSaving(true);
    try {
      if (selectedMessage?.id) {
        // Update existing message
        const { error } = await supabase
          .from('site_messages')
          .update({
            ...messageData,
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedMessage.id);

        if (error) throw error;
        showNotification('Message updated successfully', 'success');
      } else {
        // Create new message
        const { error } = await supabase
          .from('site_messages')
          .insert({
            ...messageData,
            created_by: user.id
          });

        if (error) throw error;
        showNotification('Message created successfully', 'success');
      }

      await loadMessages();
      await loadStats();
      setActiveView('list');
      setSelectedMessage(null);
    } catch (error) {
      showNotification('Error saving message: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (messageId) => {
    if (!confirm('Are you sure you want to delete this message?')) return;

    try {
      const { error } = await supabase
        .from('site_messages')
        .delete()
        .eq('id', messageId);

      if (error) throw error;

      showNotification('Message deleted successfully', 'success');
      await loadMessages();
      await loadStats();
    } catch (error) {
      showNotification('Error deleting message: ' + error.message, 'error');
    }
  };

  const handleToggleActive = async (messageId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('site_messages')
        .update({ active: !currentStatus })
        .eq('id', messageId);

      if (error) throw error;

      showNotification(
        `Message ${!currentStatus ? 'activated' : 'deactivated'} successfully`,
        'success'
      );
      await loadMessages();
    } catch (error) {
      showNotification('Error updating message: ' + error.message, 'error');
    }
  };

  const handleEdit = (message) => {
    setSelectedMessage(message);
    setActiveView('edit');
  };

  const handleNew = () => {
    setSelectedMessage(null);
    setActiveView('edit');
  };

  const handleCancel = () => {
    setSelectedMessage(null);
    setActiveView('list');
  };

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Check admin access
  const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'studio_admin';
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">Access Denied</h1>
          <p className="text-muted-foreground">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Site Messages - Admin - Solis</title>
      </Helmet>

      <div className="flex min-h-screen bg-background">
        <AdminSidebar />

        <main className="flex-1 p-6 ml-64">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-foreground mb-2">Site Messages</h1>
              <p className="text-muted-foreground">
                Manage site-wide messages with targeting and scheduling
              </p>
            </div>

            {/* Notification */}
            {notification && (
              <div
                className={`mb-6 p-4 rounded-lg ${
                  notification.type === 'success'
                    ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
                }`}
              >
                {notification.message}
              </div>
            )}

            {/* Stats */}
            {stats && stats.length > 0 && activeView === 'list' && (
              <MessageStats stats={stats} />
            )}

            {/* Content */}
            {activeView === 'list' ? (
              <MessageList
                messages={messages}
                loading={loading}
                onNew={handleNew}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggleActive={handleToggleActive}
              />
            ) : (
              <MessageEditor
                message={selectedMessage}
                onSave={handleSave}
                onCancel={handleCancel}
                saving={saving}
              />
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default AdminSiteMessages;
